'use client';

/**
 * HistoryList — shared grouped conversation list (v3 T6/T8). Extracted from
 * the standalone V2 drawer so the new desktop HistorySidebar and the
 * refitted mobile ConversationHistoryDrawer render identical rows and stay
 * in sync — a change to row styling/behavior only has to happen once.
 *
 * Reuses groupConversations/filterConversations from utils.ts. The CALLER
 * is responsible for injectActiveConversation before passing `conversations`
 * down: the caller also needs the pre-filter (but post-inject) list to
 * decide its own "fully empty" treatment (e.g. HistorySidebar's big empty
 * state vs. this component's plain "No conversations yet" text), so doing
 * the inject once at the caller avoids a second, possibly-inconsistent
 * computation here.
 *
 * NOTE: this list is never portaled (unlike a Radix Popover, which renders
 * its content outside the React tree and would need a global-CSS hover
 * workaround to reach), so plain Tailwind `group`/`group-hover:` works
 * directly.
 */

import { memo, useCallback, useMemo } from 'react';
import { Trash2 } from 'lucide-react';
import { cn, formatRelativeTime } from '@/lib/utils';
import { V3_COLORS } from '@/lib/design-tokens';
import { groupConversations, filterConversations } from './utils';
import type { ConversationRow } from './types';

export interface HistoryListProps {
  /** Raw list, already backfilled with the in-progress/active conversation
   *  via injectActiveConversation — NOT yet filtered by search. */
  conversations: ConversationRow[];
  activeConversationId: string | null;
  searchQuery: string;
  onSelect: (id: string, title: string | null) => void;
  /** `isLocalOnly` mirrors the row's ConversationRow flag — true for the
   *  injected active-conversation stand-in, which has no server record to
   *  delete. The caller (history-sidebar.tsx / conversation-history-drawer.tsx)
   *  must route a local-only delete to a local reset instead of DELETE. */
  onDelete: (id: string, isLocalOnly: boolean) => void;
  /** First fetch in flight (React Query `isPending` — no cached data yet).
   *  Renders skeleton rows instead of the list so the surface doesn't flash
   *  empty/partial and then pop when the data lands. Background refetches
   *  (data already cached) must NOT set this — the stale list stays up. */
  isPending?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  className?: string;
}

export const HistoryList = memo(function HistoryList({
  conversations,
  activeConversationId,
  searchQuery,
  onSelect,
  onDelete,
  isPending = false,
  isError = false,
  onRetry,
  className,
}: HistoryListProps) {
  const filtered = useMemo(
    () => filterConversations(conversations, searchQuery),
    [conversations, searchQuery]
  );
  const grouped = useMemo(() => groupConversations(filtered), [filtered]);

  // First fetch in flight — skeleton rows keep the list surface stable
  // instead of flashing the injected-active-row-only (or empty) state and
  // then popping when the server list lands.
  if (isPending && !isError) {
    return (
      <div className={cn('overflow-y-auto', className)}>
        <HistoryListSkeleton />
      </div>
    );
  }

  return (
    <div className={cn('overflow-y-auto', className)}>
      {/* A failed fetch must NOT collapse into "No conversations yet" —
          that reads as data loss. Same distinct error+retry treatment as
          the original drawer/popover. */}
      {isError && (
        <div className="px-[16px] py-8 text-center">
          <div className="text-[13px]" style={{ color: V3_COLORS.muted }}>
            Couldn&apos;t load your conversations.
          </div>
          {onRetry && (
            <button
              onClick={onRetry}
              className="mt-[12px] rounded-full border-[1px] px-[12px] py-1.5 text-[12.5px] font-semibold transition-colors hover:bg-black/[0.03]"
              style={{
                borderColor: V3_COLORS.warmBorderAlt,
                color: V3_COLORS.orange,
              }}
            >
              Try again
            </button>
          )}
        </div>
      )}

      {!isError && grouped.length === 0 && (
        <div
          className="px-[16px] py-8 text-center text-[13px]"
          style={{ color: V3_COLORS.muted }}
        >
          {searchQuery
            ? 'No conversations match your search'
            : 'No conversations yet'}
        </div>
      )}

      {!isError &&
        grouped.map(({ group, items }) => (
          <div key={group}>
            <div
              className="px-[10px] pb-1 pt-2 text-[11.5px] font-bold uppercase"
              style={{ color: V3_COLORS.faint, letterSpacing: '0.06em' }}
            >
              {group}
            </div>
            {items.map((conv) => (
              <HistoryRow
                key={conv.id}
                conversation={conv}
                isActive={conv.id === activeConversationId}
                onSelect={() => onSelect(conv.id, conv.title)}
                onDelete={() => onDelete(conv.id, conv.isLocalOnly ?? false)}
              />
            ))}
          </div>
        ))}
    </div>
  );
});

// ── Row ───────────────────────────────────────────────────────────

interface HistoryRowProps {
  conversation: ConversationRow;
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

const HistoryRow = memo(function HistoryRow({
  conversation,
  isActive,
  onSelect,
  onDelete,
}: HistoryRowProps) {
  const title = conversation.title || 'New conversation';

  // A `<div role="button">`, not a `<button>`, because the row needs a
  // NESTED interactive delete button — nesting <button> inside <button>
  // (what the pre-v3 drawer did) is invalid HTML.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect();
      }
    },
    [onSelect]
  );

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete();
    },
    [onDelete]
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      className="group flex cursor-pointer items-center gap-[10px] rounded-[11px] px-[12px] py-[11px] hover:bg-white/60"
      style={isActive ? { backgroundColor: '#F3EADF' } : undefined}
    >
      {/* T6 row anatomy: title (⋯/delete at its right) → preview → bottom
          meta line ("Agent · 2h ago", left-aligned). The timestamp lives
          UNDER the preview per the design, not top-right. */}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-[10px]">
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-[14px]',
              isActive ? 'font-bold' : 'font-semibold'
            )}
            style={{ color: V3_COLORS.ink }}
          >
            {title}
          </span>
          <button
            onClick={handleDeleteClick}
            // opacity (not hidden/block) so the button's space is ALWAYS
            // reserved — swapping display on hover reflowed the title width
            // and made the row text shift. Arbitrary `opacity-[0]` because
            // Bootstrap ships `.opacity-0{opacity:0!important}`, which would
            // defeat the group-hover reveal (same collision family as
            // px-*/border/rounded). `transform` folded into the same
            // transition-property list for the shared press feel (see
            // PRESSABLE's doc comment in design-tokens.ts).
            className="pointer-events-none shrink-0 rounded-[4px] p-1 text-[#cabfae] opacity-[0] transition-[opacity,color,transform] duration-[160ms] ease-out hover:bg-red-50 hover:text-red-500 group-hover:pointer-events-auto group-hover:opacity-100 motion-safe:active:scale-[0.96]"
            aria-label={`Delete conversation: ${title}`}
            tabIndex={-1}
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
        {/* The preview slot ALWAYS renders (placeholder when the row has no
            preview yet) so every row keeps the same height — hiding the line
            made a brand-new chat's row grow when its first preview arrived,
            shifting the whole list (owner decision: placeholder text).
            "No messages yet" only when that's literally true (message_count
            0 — the injected in-progress chat); a committed conversation whose
            preview just isn't available yet gets a neutral ellipsis. */}
        <span
          className={cn(
            'line-clamp-1 text-[12.5px] leading-[1.45]',
            !conversation.preview && 'italic'
          )}
          style={{
            color: conversation.preview ? V3_COLORS.muted : V3_COLORS.faint,
          }}
        >
          {conversation.preview ||
            (conversation.message_count === 0 ? 'No messages yet' : '…')}
        </span>
        <div className="flex items-center gap-[6px]">
          {conversation.had_handoff && (
            <span
              className="rounded-[10px] px-2 py-0.5 text-[11px] font-semibold"
              style={{ backgroundColor: '#EDE4D6', color: V3_COLORS.muted }}
            >
              Agent
            </span>
          )}
          <span className="text-[11.5px]" style={{ color: V3_COLORS.faint }}>
            {formatRelativeTime(
              conversation.last_message_at ?? conversation.updated_at
            )}
          </span>
        </div>
      </div>
    </div>
  );
});

// ── Loading skeleton ──────────────────────────────────────────────

/** Warm placeholder bars on the #FBF7F1 sidebar surface — same two hexes as
 *  the real rows' active/badge fills so the palette stays coherent. */
const SKELETON_BAR = '#EDE4D6';
const SKELETON_BAR_SOFT = '#F3EADF';

/**
 * Mirrors HistoryRow's exact line anatomy (px-[12px] py-[11px]; 22px title
 * line / 18px preview / 17px meta, gap-0.5) so the real rows replace the
 * placeholders with zero layout shift — the row grid simply "fills in".
 */
function HistoryListSkeleton() {
  // Deterministic per-row title widths — visual variety without randomness.
  const titleWidths = ['62%', '48%', '70%', '54%'];
  return (
    <div aria-hidden="true" className="animate-pulse">
      {/* Group header slot (e.g. "TODAY") */}
      <div className="flex h-[26px] items-end px-[10px] pb-1 pt-2">
        <div
          className="h-[11px] w-[46px] rounded-[6px]"
          style={{ backgroundColor: SKELETON_BAR }}
        />
      </div>
      {titleWidths.map((width, i) => (
        <div key={i} className="flex flex-col gap-0.5 px-[12px] py-[11px]">
          <div className="flex h-[22px] items-center">
            <div
              className="h-[14px] rounded-[6px]"
              style={{ width, backgroundColor: SKELETON_BAR }}
            />
          </div>
          <div className="flex h-[18px] items-center">
            <div
              className="h-[12px] w-[86%] rounded-[6px]"
              style={{ backgroundColor: SKELETON_BAR_SOFT }}
            />
          </div>
          <div className="flex h-[17px] items-center">
            <div
              className="h-[11px] w-[44px] rounded-[6px]"
              style={{ backgroundColor: SKELETON_BAR_SOFT }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
